/**
 * System section — admin panel (brief §6.7).
 *
 * Internal tabs, switched via the `?tab=` query param so any tab is
 * deep-linkable. Drawer state (audit log row detail) lives at `?id=`.
 *
 *   ?tab=health  — API/DB/build health overview
 *   ?tab=jobs    — Background jobs + ingestion
 *   ?tab=analytics-audit — Canonical analytics event/outbox audit
 *   ?tab=audit   — Audit log
 *
 * Per-league simulation controls (clock + round status + WC reset) live
 * under Game Data → EPL/WC → Simulation. Feature flags stay out of the
 * nav until there is a real flag registry to operate.
 */

import { useSearchParams } from 'react-router-dom';

import { PageHeader } from '@/components/PageHeader';
import { cn } from '@/lib/utils';

import { AuditLogTab } from './AuditLogTab';
import { HealthTab } from './HealthTab';
import { JobsTab } from './JobsTab';
import { AnalyticsEventsTab } from '../users-and-agents/AnalyticsEventsTab';

type TabId = 'health' | 'jobs' | 'analytics-audit' | 'audit';

interface SystemTab {
  id: TabId;
  label: string;
}

const TABS: SystemTab[] = [
  { id: 'health', label: 'Health' },
  { id: 'jobs', label: 'Background jobs + ingestion' },
  { id: 'analytics-audit', label: 'Analytics Audit' },
  { id: 'audit', label: 'Audit log' },
];

const DEFAULT_TAB: TabId = 'health';

export function SystemPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const tab: TabId = (TABS.find((t) => t.id === rawTab)?.id as TabId | undefined) ?? DEFAULT_TAB;

  const setTab = (next: TabId) => {
    // Drop ?id= when switching tabs — it belongs to the tab it was opened in.
    const sp = new URLSearchParams();
    sp.set('tab', next);
    setSearchParams(sp, { replace: false });
  };

  return (
    <>
      <PageHeader
        title="System"
        description="Health, jobs, analytics delivery, and audit log."
      />

      <div className="px-8 py-6">
        <div className="mb-4 flex flex-wrap items-center gap-1 border-b">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors',
                tab === t.id
                  ? 'border-[#00D4AA] text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'health' ? <HealthTab /> : null}
        {tab === 'jobs' ? <JobsTab /> : null}
        {tab === 'analytics-audit' ? <AnalyticsEventsTab /> : null}
        {tab === 'audit' ? <AuditLogTab /> : null}
      </div>
    </>
  );
}
